import { createTheme } from "@mui/material/styles";
import { Fonts } from "./Fonts";

let themeValues = {
  shadows: Array(25).fill("none"),
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1440,
    },
  },
  elevationLow: 1,
  elevationMid: 3,
  roundness: 4,
  palette: {
    common: {
      black: "rgba(33, 33, 33, 1)",
      white: "rgba(255, 255, 255, 1)",
    },
    background: {
      paper: "#fff",
      default: "#F5F5F5",
    },
    primary: {
      main: "#0E3F51",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#38748D",
      contrastText: "#fff",
    },
    highlight: {
      main: "#63BACE",
      hover: "#3daac2",
      contrastText: "#fff",
    },
    error: {
      main: "#FF3D00",
      contrastText: "#fff",
    },
    warning: {
      main: "#FF8256",
      contrastText: "#fff",
    },
    info: {
      main: "#707070",
    },
    success: {
      main: "#7CB342",
      contrastText: "#fff",
    },
    text: {
      primary: "#212121",
      secondary: "#707070",
      disabled: "#757575",
      hint: "#9E9E9E",
    },
    grayShade: {
      "900": "#AEAEAE",
      "800": "#707070",
      "700": "#757575",
      "500": "#9E9E9E",
      "300": "#E0E0E0",
      "200": "#EEEEEE",
      "100": "#F5F5F5",
      "50": "#FAFAFA",
    },
    white: {
      main: "#FFFFFF",
    },
    whiteOpacity: {
      "90": "rgba(255, 255, 255, 0.9)",
      "60": "rgba(255, 255, 255, 0.6)",
      "50": "rgba(255, 255, 255, 0.5)",
      "30": "rgba(255, 255, 255, 0.3)",
    },
    pacificBlue: {
      // main:"#039BE5"
      main: "#8E24AA",
    },
    Yellow: {
      main: "#FFB300",
    },
  },
};

/* Text Styles */
themeValues = {
  ...themeValues,
  typography: {
    fontFamily: "Roboto",
    /* Basic Color Classes */
    white: { color: themeValues.palette.white.main },
    white_60: { color: themeValues.palette.white.main, opacity: 0.6 },
    black: { color: themeValues.palette.text.primary },
    primary: { color: themeValues.palette.primary.main },
    secondary: { color: themeValues.palette.secondary.main },
    red: { color: themeValues.palette.error.main },
    orange: { color: themeValues.palette.warning.main },
    green: { color: themeValues.palette.success.main },

    /* Basic Font Weight classes */
    regular: { fontWeight: "400 !important" },

    /* Basic Alignment Classes */
    center: { textAlign: "center" },

    /* Text Styles */
    body1: {
      ...Fonts.RubikRegular,
      fontSize: 16,
      lineHeight: 1.5,
      letterSpacing: 0.5,
      color: themeValues.palette.text.primary,
    },
    paragraph: {
      ...Fonts.RubikRegular,
      fontSize: 16,
      lineHeight: 1.75,
      letterSpacing: 0.5,
      color: themeValues.palette.text.primary,
    },
    body2: {
      ...Fonts.RobotoRegular,
      fontSize: 16,
      lineHeight: 1.5,
      letterSpacing: 0.5,
      color: themeValues.palette.text.primary,
    },
    h1: {
      ...Fonts.RubikMedium,
      fontSize: 20,
      lineHeight: 1.2,
      color: themeValues.palette.primary.main,
    },
    h2: {
      ...Fonts.RubikMedium,
      fontSize: 18,
      lineHeight: 1.22,
      color: themeValues.palette.text.primary,
    },
    h3: {
      ...Fonts.RubikMedium,
      fontSize: 16,
      lineHeight: 1.375,
      letterSpacing: 0.15,
      textTransform: "uppercase",
      color: themeValues.palette.text.primary,
    },
    cardTitle: {
      ...Fonts.RobotoMedium,
      fontSize: 14,
      lineHeight: 1.143,
      letterSpacing: 0.5,
    },
    overline: {
      ...Fonts.RobotoMedium,
      fontSize: 12,
      lineHeight: 1.143,
      color: themeValues.palette.primary.main,
      letterSpacing: 2,
      marginBottom: 16,
    },
    subheader: {
      ...Fonts.RobotoMedium,
      fontSize: 12,
      lineHeight: 1.333,
      color: themeValues.palette.grayShade["700"],
      textTransform: "uppercase",
      letterSpacing: 2,
      marginBottom: 16,
    },
    caption: {
      fontSize: 14,
      lineHeight: 1.429,
    },
    small_caption: {
      fontSize: 12,
      lineHeight: 1,
      color: themeValues.palette.grayShade["700"],
    },
    link: {
      color: themeValues.palette.secondary.main,
    },

    /* Tab */
    tabLabel: {
      ...Fonts.RubikMedium,
      fontSize: 14,
      lineHeight: 1.143,
      letterSpacing: 1.25,
      textTransform: "uppercase",
    },

    messageText: {
      ...Fonts.RubikRegular,
      fontSize: 14,
      lineHeight: 1.429,
      letterSpacing: 0.5,
      color: themeValues.palette.text.primary,
    },
  },
};

/* Overrides */
themeValues = {
  ...themeValues,
  overrides: {
    MuiChip: {
      root: {
        borderRadius: 3,
      },
    },
    MuiBadge: {
      badge: {
        padding: "0 2px",
        fontSize: 10,
        height: 14,
        minWidth: 14,
        width: 14,
      },
    },
    MuiFormHelperText: {
      root: {
        ...themeValues.typography.small_caption,
        display: "block",
        marginTop: 8,
        color: themeValues.palette.grayShade["700"],
      },
    },
  },
};

/* Helpers */
themeValues = {
  ...themeValues,
  noPadding: {
    top: { paddingTop: 0 + " ! important" },
    bottom: { paddingBottom: 0 + " ! important" },
    left: { paddingLeft: 0 + " ! important" },
    right: { paddingRight: 0 + " ! important" },
  },
  noMargin: {
    top: { marginTop: 0 + " ! important" },
    bottom: { marginBottom: 0 + " ! important" },
    left: { marginLeft: 0 + " ! important" },
    right: { marginRight: 0 + " ! important" },
  },
};

/* Transitions */
themeValues = {
  ...themeValues,

  transition: {
    all_fast: {
      transition: "all ease-out 140ms",
    },
  },
};

/* Containers */
themeValues = {
  ...themeValues,

  sectionContainer: {
    overflow: "hidden",
    marginBottom: 48,
  },

  elevatedSection: {
    padding: "28px 24px",
    marginBottom: 48,
  },

  sectionHeader: {
    display: "flex",
    flexWrap: "nowrap",
    marginBottom: 32,
  },

  innerSectionContainer: {
    display: "flex",
    justifyContent: "flex-start",
    flexWrap: "nowrap",
    marginBottom: 20,

    "& h1, & h2, & h3, & h4, & h5, & h6": {
      marginBottom: 0,
    },
  },

  sectionHeaderPrimary: {
    backgroundColor: themeValues.palette.primary.main,
    padding: "24px 28px",
  },

  sectionContentContainer: {
    padding: "24px !important",

    "& h1, & h2, & h3, & p": {
      marginBottom: "1em",
    },
  },

  sectionOptionsButton: {
    marginTop: -12,
    marginLeft: 20,
  },

  sectionButtonFooter: {
    display: "flex",
    justifyContent: "flex-end",

    "& button": {
      minWidth: 250,
      marginLeft: 24,

      "&:first-of-type": {
        marginLeft: 0,
      },
    },
  },

};

/* Tabs */
themeValues = {
  ...themeValues,

  tabsContainer: {
    marginTop: 0,

    "& > .MuiGrid-grid-xs-12": {
      paddingTop: 0,
    },

    "& .MuiAppBar-root": {
      backgroundColor: themeValues.palette.primary.main,
      color: themeValues.palette.primary.contrastText,

      "& .MuiTabs-indicator": {
        backgroundColor: themeValues.palette.white.main,
        height: 3,
      },
    },

    "& .MuiTab-textColorInherit": {
      opacity: "0.5",

      "&.Mui-selected": {
        opacity: 1,
      },
    },
  },
};

/* Forms */
themeValues = {
  ...themeValues,

  form: {
    width: "100%",
    marginBottom: 0,

    "& .MuiFormControl-root": {
      width: "100%",
    },
  },

  inputField: {
    marginBottom: 28,
    width: "100%",

    "& .MuiFormLabel-root": {
      color: "rgba(0,0,0,0.6)",

      "&.Mui-focused": {
        color: themeValues.palette.common.black,
      },
    },
  },

  submitButton: {
    width: "100%",
  },

  inputFieldOpaqueWhite: {
    marginBottom: 28,
    width: "100%",

    "& .MuiOutlinedInput-root": {
      backgroundColor: "white",

      "&.Mui-focused": {
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: themeValues.palette.grayShade["500"],
        },
      },
    },

    "& .MuiFormHelperText-root": {
      ...themeValues.typography.small_caption,
      display: "block",
      marginTop: 8,
      color: themeValues.palette.whiteOpacity["60"],
    },

    "& .MuiOutlinedInput-notchedOutline": {
      borderWidth: 0,
    },
  },

  helperText: {
    ...themeValues.typography.small_caption,
    display: "block",
    marginTop: 8,
    paddingLeft: 14,
    color: themeValues.palette.grayShade["700"],
  },

  helperTextLink: {
    "&:hover": {
      color: themeValues.palette.grayShade["500"],
    },
  },

  multiselectCheckbox: {
    padding: 5,
    marginRight: 10,
  },

  formControlLabel: {
    ...themeValues.typography.small_caption,
    display: "block",
    marginBottom: 8,
    color: themeValues.palette.grayShade["500"],
  },
};

/* Navigation */
themeValues = {
  ...themeValues,

  breadcrumbs: {
    marginBottom: 20,

    dividerVertical: {
      position: "absolute",
      height: 14,
      borderColor: themeValues.palette.secondary.main,
      top: 20,
      left: 8,
    },

    "& .MuiBreadcrumbs-li a": {
      position: "relative",
      display: "flex",
      alignItems: "center",
      ...themeValues.transition.all_fast,

      "&:hover": {
        opacity: 0.7,
      },

      "& svg": {
        width: "0.9em",
        height: "0.9em",
        marginRight: 14,
      },
    },
  },
  leveledBreadcrumbs: {
    marginBottom: 28,

    "& .MuiBreadcrumbs-li a": {
      ...themeValues.typography.small_caption,

      "& svg": {
        fontSize: "1rem",
      },
    },
  },
};

/* Machine / Notification Cards  */
themeValues = {
  ...themeValues,

  /* GRID (Multiple Columns) */
  itemcardGrid: {},
  itemcardGridSection: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 48,
  },
  itemcardGridHeadline: {
    width: "100%",
    display: "block",
    marginBottom: 20,
  },
  itemcardGridItem: {
    width: "calc(50% - 12px)",
    marginBottom: 20,
    "&:nth-of-type(odd)": {
      marginRight: 24,
    },
  },

  /* List (One column) */
  itemcardList: {
    "& .cardItem": {
      marginBottom: 20,
    },
  },

  itemcardButtonFooter: {
    display: "block",
    width: "100%",
  },

  itemcard: {
    wrapper: {
      width: "100%",
      borderRadius: themeValues.roundness,
    },
    actionAreaWrapper: {
      display: "flex",
      alignItems: "normal",
      flexDirection: "row",
    },
    cardInner: {
      padding: "16px 16px 10px 16px",
      flexGrow: 1,
      borderTopWidth: 6,
      borderTopStyle: "solid",
      width: "80%",
      borderColor: themeValues.palette.grayShade["100"],
    },
    cardImageContainer: {
      flexShrink: 1,
      backgroundColor: themeValues.palette.grayShade["100"],
      width: "30%",
      overflow: "hidden",
    },
    cardTextContainer: {},
    header: {
      display: "flex",
      alignItems: "flex-start",
    },
    title: {
      ...themeValues.typography.cardTitle,
      maxWidth: "100%",
      maxHeight: 32, // 2 * Text Height to cut off elements
      flexGrow: 1,
      textOverflow: "ellipsis",
      whiteSpace: "normal",
      overflow: "hidden",
      marginBottom: "0.5em",
    },
    details: {
      ...themeValues.typography.small_caption,
      lineHeight: 1.6,
    },
    tagsRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
      marginTop: 8,
      marginBottom: 8,
    },
    tagChip: {
      display: "inline-flex",
      alignItems: "center",
      minHeight: 24,
      border: "1px solid rgba(0, 0, 0, 0.36)",
      borderRadius: 16,
      padding: "2px 12px",
      color: "rgba(0, 0, 0, 0.87)",
      fontSize: 13,
      lineHeight: 1.2,
      backgroundColor: "transparent",
      maxWidth: "100%",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },

    status: {
      cardStatusGray: {
        borderTopColor: themeValues.palette.grayShade["900"],
      },
      cardStatusPacificBlue: {
        borderTopColor: themeValues.palette.pacificBlue.main,
      },
      cardStatusGreen: {
        borderTopColor: themeValues.palette.success.main,
      },
      cardStatusOrange: {
        borderTopColor: themeValues.palette.warning.main,
      },
      cardStatusRed: {
        borderTopColor: themeValues.palette.error.main,
      },
      cardStatusYellow: {
        borderTopColor: themeValues.palette.Yellow.main,
      },

      // cardStatusDarkMagenta: {
      //   // borderTopColor: themeValues.palette.pacificBlue.main,
      //   borderTopColor : "rgb(142, 36, 170)"
      // },
    },

    iconTextInline: {
      flex: 1,
      flexDirection: "row",

      "& .iconTextContainer": {
        marginRight: 16,
      },
      "& .iconTextContainer:last-of-type": {
        marginRight: 0,
      },
    },
  },
};

/* Icon Text List */
themeValues = {
  ...themeValues,

  searchIconsList: {
    display: "flex",
    justifyContent: "space-between",
    padding: 0,
    margin: 0,

    "& li": {
      listStyle: "none",
      margin: "0% 3%",
    },

    "& a": {
      ...themeValues.typography.link,
    },
  },
  iconTextList: {
    display: "flex",
    flexDirection: "column",
    padding: 0,
    margin: 0,

    "& li": {
      listStyle: "none",
      marginBottom: 2,
    },

    "& a": {
      ...themeValues.typography.link,
    },
  },
  detailsDivider: {
    height: "auto",
    marginLeft: 24,
    marginRight: 22,
  },
};

/* Level Menu */
themeValues = {
  ...themeValues,

  levelMenu: {
    "& .MuiMenu-list": {
      "& .MuiListItem-root": {
        justifyContent: "space-between",
        "& .MuiCheckbox-root": {
          marginLeft: 32,
          padding: 8,
        },
      },
    },

    imageContainer: {
      width: 48,
      height: 48,
      overflow: "hidden",
      marginRight: 16,

      "& img": {
        height: "100%",
        width: "auto",
      },
    },

    level1: {
      ...themeValues.typography.small_caption,
      paddingTop: 4,
      paddingBottom: 4,
      marginTop: 16,

      "&:first-of-type": {
        marginTop: 0,
      },
    },
    level2: {
      paddingLeft: 16,

      "& .MuiListItem-root": {
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        borderBottom: "1px solid " + themeValues.palette.grayShade["300"],

        "&:last-of-type": {
          borderBottom: "none",
        },
      },
    },
  },
};

const theme = createTheme(themeValues);
export { theme as Theme247 };
